import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import LeadCaptureForm from '../../components/LeadCaptureForm';
import { trackEvent } from '../../utils/analytics';
import { filterModelGroups, getModelById, PRICES_AS_OF } from '../../data/valuationModels';
import {
  valueTaxi,
  retentionSeries,
  article,
  formatINR,
  formatLakh,
  expectedKmFor,
  CITIES,
  REGISTRATION_OPTIONS,
  CONDITION_OPTIONS,
  PAPERS_OPTIONS,
} from '../../utils/taxiValuation';
import './TaxiValuation.css';

const BASE_URL = 'https://www.buyselltaxi.com';
const PAGE_URL = `${BASE_URL}/taxi-valuation`;
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 21 }, (_, i) => CURRENT_YEAR - i);

/**
 * A model's fuel list says "CNG"; the market prices factory and retro-fitted CNG
 * differently, so the dropdown splits what the catalogue keeps together.
 */
const fuelChoices = (model) => {
  if (!model) return [];
  return model.fuels.flatMap((f) =>
    f === 'CNG' ? ['CNG (factory-fitted)', 'CNG (retro-fitted)'] : [f],
  );
};

const FAQS = [
  {
    q: 'Why is my taxi worth less than the same car used privately?',
    a: 'Because it has done five to six times the distance and the RC says "transport". A privately-used car covers 10,000–12,000 km a year; a taxi covers 60,000–80,000. Buyers also know a commercial vehicle has run long shifts with multiple drivers. At five years, a private car typically holds about half its price while the same model on a taxi permit holds a little over a third.',
  },
  {
    q: 'How much does a taxi depreciate every year in India?',
    a: 'A commercial taxi loses roughly 20% the moment it is registered and driven out, and about 12–15% of its remaining value every year after that. By year 5 it is worth around 36% of what an equivalent new vehicle costs today, and by year 10 around 15% — MUVs like the Innova and Traveller hold considerably more because operators keep rebuilding them.',
  },
  {
    q: 'Does the Delhi 10-year diesel rule affect my taxi price?',
    a: 'Heavily. A diesel commercial vehicle cannot ply in NCR beyond 10 years and a petrol or CNG one beyond 15. The market starts discounting two to three years before that date, and once the cap passes the vehicle can only be sold for transfer to another state or scrapped — usually for about half of what the same vehicle fetches outside NCR.',
  },
  {
    q: 'Is a CNG taxi worth more than a petrol or diesel one?',
    a: 'Yes, in the used commercial market. A factory-fitted CNG vehicle typically resells about 5–6% above the equivalent petrol version because its running cost per km is what the next owner will earn on. Retro-fitted kits do not command the same premium, and a pre-2020 BS4 diesel now sells at a discount because of tightening restrictions.',
  },
  {
    q: 'How accurate is this taxi valuation calculator?',
    a: 'It gives a realistic market range based on age, odometer, fuel, permit type, condition, ownership and your city rules — priced against what commercial vehicles actually close at, not what sellers ask. Final price always depends on physical inspection, service history and paperwork. For a firm number, request a free callback and our team will value your vehicle against live buyer demand.',
  },
  {
    q: 'Should I sell my taxi now or run it another year?',
    a: 'Run the numbers above and compare the value today with the projection a year out. Commercial vehicles lose value faster than almost any other asset, and if you are within three years of your city\'s age cap the drop accelerates sharply. In most cases, selling before the next fitness renewal recovers more than another year of earnings does.',
  },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: 'Taxi Valuation Calculator', item: PAGE_URL },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Taxi Valuation Calculator',
    url: PAGE_URL,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
    description:
      'Free valuation calculator for used taxis and commercial vehicles in India, using commercial depreciation rates, permit type, odometer and city age-cap rules.',
    provider: { '@type': 'Organization', name: 'Buy Sell Taxi', url: BASE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
];

/**
 * Retention curves, commercial against private. The whole argument of this page
 * is in the gap between the two lines, so it gets drawn rather than described.
 */
const RetentionChart = ({ curve, age }) => {
  const series = retentionSeries(curve, 15);
  const W = 640;
  const H = 240;
  const pad = { top: 16, right: 16, bottom: 28, left: 40 };
  const x = (a) => pad.left + (a / 15) * (W - pad.left - pad.right);
  const y = (v) => pad.top + (1 - v) * (H - pad.top - pad.bottom);
  const path = (key) => series.map((p, i) => `${i ? 'L' : 'M'}${x(p.age).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const marker = series[Math.min(age, 15)];

  return (
    <svg className="tv-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Value retained by a taxi versus a private car, by age">
      {[0, 0.25, 0.5, 0.75, 1].map((v) => (
        <g key={v}>
          <line className="tv-chart-grid" x1={pad.left} x2={W - pad.right} y1={y(v)} y2={y(v)} />
          <text className="tv-chart-label" x={pad.left - 8} y={y(v) + 4} textAnchor="end">{v * 100}%</text>
        </g>
      ))}
      {[0, 5, 10, 15].map((a) => (
        <text key={a} className="tv-chart-label" x={x(a)} y={H - 8} textAnchor="middle">{a}y</text>
      ))}
      <path className="tv-chart-line tv-chart-private" d={path('private')} />
      <path className="tv-chart-line tv-chart-taxi" d={path('taxi')} />
      {marker && (
        <g>
          <line className="tv-chart-marker" x1={x(marker.age)} x2={x(marker.age)} y1={pad.top} y2={H - pad.bottom} />
          <circle className="tv-chart-dot tv-chart-dot-private" cx={x(marker.age)} cy={y(marker.private)} r="4" />
          <circle className="tv-chart-dot tv-chart-dot-taxi" cx={x(marker.age)} cy={y(marker.taxi)} r="4" />
        </g>
      )}
    </svg>
  );
};

const TaxiValuation = () => {
  useSEO({
    title: 'Taxi Valuation Calculator – What Is My Taxi Worth in 2026?',
    description:
      'Free taxi valuation calculator for India. Get the resale value of your used taxi or commercial vehicle using real commercial depreciation rates, permit type, odometer, fuel and city age-cap rules.',
    keywords:
      'taxi valuation calculator, what is my taxi worth, used taxi price India, taxi resale value, commercial vehicle valuation India, taxi depreciation rate, sell my taxi price, taxi value calculator Delhi, second hand taxi price, Innova Crysta taxi resale value, Dzire taxi resale value',
    url: PAGE_URL,
    image: `${BASE_URL}/og/landing/taxi-valuation.png`,
    jsonLd,
  });

  const [modelId, setModelId] = useState('maruti-dzire');
  const [modelQuery, setModelQuery] = useState('');
  const [manualPrice, setManualPrice] = useState('');
  const [year, setYear] = useState(String(CURRENT_YEAR - 4));
  const [km, setKm] = useState('240000');
  const [fuel, setFuel] = useState('CNG (factory-fitted)');
  const [cityId, setCityId] = useState('delhi');
  const [owners, setOwners] = useState('1');
  const [registration, setRegistration] = useState('commercial');
  const [condition, setCondition] = useState('good');
  const [papers, setPapers] = useState('valid');
  const [accident, setAccident] = useState(false);

  const model = getModelById(modelId);
  const fuels = fuelChoices(model);

  // The search narrows the picker; showing every group again when nothing
  // matches would hide the fact that we simply do not have the model.
  const visibleGroups = useMemo(() => filterModelGroups(modelQuery), [modelQuery]);

  // A <select> whose value is not among its options renders the first one
  // instead, which would show "Other" while the result below still prices the
  // vehicle the seller picked. Keep the selection in the list either way.
  const pickerGroups = useMemo(() => {
    if (modelId === 'other') return visibleGroups;
    if (visibleGroups.some((g) => g.models.some((m) => m.id === modelId))) return visibleGroups;
    const selected = getModelById(modelId);
    return selected ? [{ label: 'Selected', models: [selected] }, ...visibleGroups] : visibleGroups;
  }, [visibleGroups, modelId]);

  // Switching model can strand the fuel selection on something that model never
  // came with (a diesel Eeco), so reset it rather than silently mispricing.
  const onModelChange = (id) => {
    setModelId(id);
    const next = getModelById(id);
    const allowed = fuelChoices(next);
    if (!allowed.includes(fuel)) setFuel(allowed[0]);
  };

  // A <select> whose selected option has just been filtered out shows the wrong
  // vehicle, so searching moves the selection to the first thing it found.
  useEffect(() => {
    const ids = visibleGroups.flatMap((g) => g.models.map((m) => m.id));
    if (!modelQuery || !ids.length || ids.includes(modelId)) return;
    onModelChange(ids[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleGroups]);

  const result = useMemo(
    () =>
      valueTaxi({
        model,
        year: Number(year),
        km: Number(km),
        fuel,
        cityId,
        owners: Number(owners),
        registration,
        condition,
        papers,
        accident,
        newPrice: manualPrice ? Number(manualPrice) : undefined,
        referenceYear: CURRENT_YEAR,
      }),
    [model, year, km, fuel, cityId, owners, registration, condition, papers, accident, manualPrice],
  );

  // One event per page view, on the first usable result — enough to see which
  // models people are valuing without flooding GA on every keystroke.
  const tracked = useRef(false);
  useEffect(() => {
    if (!result || tracked.current) return;
    tracked.current = true;
    trackEvent('valuation_calculated', { model: modelId, year, city: cityId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const expected = model ? expectedKmFor(model.curve || 'car', result?.age ?? 0) : 0;
  const leadMessage = result
    ? `${year} ${model.brand} ${model.model} · ${Number(km).toLocaleString('en-IN')} km · ${fuel} · ${
        REGISTRATION_OPTIONS.find((r) => r.id === registration)?.label
      } · ${CITIES.find((c) => c.id === cityId)?.label}. Calculator estimate: ${formatINR(result.low)}–${formatINR(result.high)}.`
    : '';

  return (
    <div className="tv-page">

      {/* Hero */}
      <section className="tv-hero">
        <h1>Taxi Valuation Calculator</h1>
        <p className="tv-hero-sub">
          What is your taxi actually worth today? This calculator prices commercial vehicles on
          commercial rules — taxi-scale kilometres, permit type, fitness papers and your city's
          age cap — not the private-car depreciation every other calculator uses.
        </p>
      </section>

      {/* Calculator */}
      <section className="tv-tool" id="calculator">
        <form className="tv-form" onSubmit={(e) => e.preventDefault()} data-testid="valuation-form">
          <h2 className="tv-form-title">Your vehicle</h2>

          <label className="tv-field">
            <span>Model</span>
            <input
              type="search"
              className="tv-model-search"
              placeholder="Search 250+ models — try “dzire”, “crysta”, “traveller”"
              value={modelQuery}
              onChange={(e) => setModelQuery(e.target.value)}
              data-testid="tv-model-search"
            />
            <select value={modelId} onChange={(e) => onModelChange(e.target.value)} data-testid="tv-model">
              {pickerGroups.map(({ label, models }) => (
                <optgroup key={label} label={label}>
                  {models.map((m) => (
                    <option key={m.id} value={m.id}>{m.brand} {m.model}</option>
                  ))}
                </optgroup>
              ))}
              <option value="other">Other — not listed</option>
            </select>
            {modelQuery && !visibleGroups.length && (
              <small className="tv-hint" data-testid="tv-model-nomatch">
                Nothing matches “{modelQuery}”. Clear the search, or pick “Other — not listed” and
                enter what a new one costs today.
              </small>
            )}
          </label>

          {model?.manualPrice && (
            <label className="tv-field">
              <span>Ex-showroom price of a new equivalent (₹)</span>
              <input
                type="number"
                inputMode="numeric"
                value={manualPrice}
                onChange={(e) => setManualPrice(e.target.value)}
                placeholder="e.g. 850000"
                data-testid="tv-manual-price"
              />
              <small>What a comparable new vehicle costs today. Used prices are set against the new price, not your old invoice.</small>
            </label>
          )}

          <div className="tv-field-row">
            <label className="tv-field">
              <span>Year of registration</span>
              <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="tv-year">
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>

            <label className="tv-field">
              <span>Odometer (km)</span>
              <input
                type="number"
                inputMode="numeric"
                value={km}
                onChange={(e) => setKm(e.target.value)}
                min="0"
                step="1000"
                data-testid="tv-km"
              />
              <small>
                {article(result?.age ?? 0) === 'an' ? 'An' : 'A'} {result?.age ?? 0}-year-old commercial vehicle
                typically shows about {expected.toLocaleString('en-IN')} km.
              </small>
            </label>
          </div>

          <div className="tv-field-row">
            <label className="tv-field">
              <span>Fuel</span>
              <select value={fuel} onChange={(e) => setFuel(e.target.value)} data-testid="tv-fuel">
                {fuels.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>

            <label className="tv-field">
              <span>City</span>
              <select value={cityId} onChange={(e) => setCityId(e.target.value)} data-testid="tv-city">
                {CITIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
          </div>

          <label className="tv-field">
            <span>Registration &amp; permit</span>
            <select value={registration} onChange={(e) => setRegistration(e.target.value)} data-testid="tv-registration">
              {REGISTRATION_OPTIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </label>

          <div className="tv-field-row">
            <label className="tv-field">
              <span>Owners so far</span>
              <select value={owners} onChange={(e) => setOwners(e.target.value)} data-testid="tv-owners">
                <option value="1">1st owner</option>
                <option value="2">2nd owner</option>
                <option value="3">3rd owner</option>
                <option value="4">4th owner or later</option>
              </select>
            </label>

            <label className="tv-field">
              <span>Fitness, permit &amp; insurance</span>
              <select value={papers} onChange={(e) => setPapers(e.target.value)} data-testid="tv-papers">
                {PAPERS_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </label>
          </div>

          <label className="tv-field">
            <span>Condition</span>
            <select value={condition} onChange={(e) => setCondition(e.target.value)} data-testid="tv-condition">
              {CONDITION_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>

          <label className="tv-check">
            <input type="checkbox" checked={accident} onChange={(e) => setAccident(e.target.checked)} data-testid="tv-accident" />
            <span>Major accident or structural repair on record</span>
          </label>
        </form>

        <div className="tv-result" data-testid="valuation-result">
          {!result ? (
            <div className="tv-result-empty">
              <h2>Tell us what you drive</h2>
              <p>
                Pick your model and year to see a valuation. If your model is not listed, choose
                <strong> Other</strong> and enter what a comparable new vehicle costs today.
              </p>
            </div>
          ) : (
            <>
              <div className="tv-headline">
                <p className="tv-headline-label">Estimated market value today</p>
                <p className="tv-headline-range" data-testid="tv-range">
                  {formatLakh(result.low)} – {formatLakh(result.high)}
                </p>
                <p className="tv-headline-fair">Most likely: <strong>{formatINR(result.fair)}</strong></p>
              </div>

              <div className="tv-quotes">
                <div className="tv-quote">
                  <span className="tv-quote-label">Quick sale to a dealer</span>
                  <strong>{formatINR(result.dealerOffer)}</strong>
                  <small>Money today, no waiting, no enquiries to handle.</small>
                </div>
                <div className="tv-quote tv-quote-highlight">
                  <span className="tv-quote-label">List it yourself at</span>
                  <strong>{formatINR(result.askingPrice)}</strong>
                  <small>Leaves the negotiating room buyers expect on a commercial vehicle.</small>
                </div>
              </div>

              {result.notes.map((n, i) => (
                <p key={i} className={`tv-note tv-note-${n.tone}`}>{n.text}</p>
              ))}

              <div className="tv-depreciation">
                <p>
                  {article(result.age) === 'an' ? 'An' : 'A'} {result.age}-year-old <strong>{model.model}</strong> on a commercial permit holds{' '}
                  <strong>{result.retentionPct.toFixed(0)}%</strong> of what a new one costs today. The same
                  model registered privately would hold about <strong>{result.privateRetentionPct.toFixed(0)}%</strong> —
                  running it as a taxi has cost you roughly{' '}
                  <strong>{formatLakh((result.privateRetentionPct - result.retentionPct) / 100 * result.newPrice)}</strong>{' '}
                  in extra depreciation.
                </p>
              </div>

              <div className="tv-breakdown">
                <h3>How we got there</h3>
                <div className="tv-breakdown-base">
                  <span>Base value — {result.age} years of commercial depreciation on a {formatLakh(result.newPrice)} vehicle</span>
                  <strong>{formatINR(result.base)}</strong>
                </div>
                <ul className="tv-breakdown-list">
                  {result.breakdown.map((b) => (
                    <li key={b.label} className={b.pct >= 0 ? 'tv-up' : 'tv-down'}>
                      <div className="tv-breakdown-head">
                        <span>{b.label}</span>
                        <strong>{b.pct > 0 ? '+' : ''}{b.pct.toFixed(1)}%</strong>
                      </div>
                      <small>{b.detail}</small>
                    </li>
                  ))}
                </ul>
                <div className="tv-breakdown-total">
                  <span>Estimated value</span>
                  <strong>{formatINR(result.fair)}</strong>
                </div>
                {result.isScrapFloor && (
                  <p className="tv-note tv-note-warn">
                    The calculation lands below what the vehicle is worth as parts and metal, so we have
                    floored it at scrap value. At this point condition matters more than any number here.
                  </p>
                )}
              </div>

              <div className="tv-projection">
                <h3>What waiting costs you</h3>
                <div className="tv-projection-cards">
                  {result.projection.map((p) => (
                    <div key={p.yearsFromNow} className={`tv-projection-card${p.yearsFromNow === 0 ? ' tv-projection-now' : ''}`}>
                      <span className="tv-projection-when">
                        {p.yearsFromNow === 0 ? 'Today' : `In ${p.yearsFromNow} year${p.yearsFromNow > 1 ? 's' : ''}`}
                      </span>
                      <strong>{formatINR(p.value)}</strong>
                      <small>{p.age} yrs · {Math.round(p.km / 1000)}k km</small>
                    </div>
                  ))}
                </div>
                <p className="tv-projection-note">
                  {result.projection[0].value - result.projection[1].value > 0 ? (
                    <>
                      Assuming it keeps running normal commercial kilometres. Another 12 months of work costs
                      roughly <strong>{formatINR(result.projection[0].value - result.projection[1].value)}</strong> in
                      value — worth weighing against what the vehicle earns in that year.
                    </>
                  ) : (
                    <>
                      This vehicle has already depreciated as far as it goes — it is worth its parts and metal
                      either way. There is no value left to protect by selling early, so run it for as long as it
                      earns and its papers allow.
                    </>
                  )}
                </p>
              </div>

              <div className="tv-actions">
                <Link to="/seller/register" className="tv-btn-primary">List It Free on Buy Sell Taxi</Link>
                <Link to="/vehicles" className="tv-btn-secondary">See What Similar Taxis Sell For</Link>
                {/* "Replacing it? New {model} prices" linked into /new-taxi
                    here — restore when that section returns from its rework.
                    Until then the link would just bounce off a redirect. */}
              </div>

              <p className="tv-disclaimer">
                An estimate, not an offer. Prices are benchmarked against ex-showroom figures as of {PRICES_AS_OF} and
                what commercial vehicles actually close at — final value always depends on physical inspection,
                service history and paperwork.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Why taxis are different */}
      <section className="tv-explainer">
        <div className="tv-explainer-inner">
          <h2>Why Taxis Depreciate Faster Than Private Cars</h2>
          <p>
            The same Dzire, bought the same week, is worth very different money five years later
            depending on what its RC says. A privately-driven car covers 10,000–12,000 km a year. A
            taxi covers 60,000–80,000 — five to six years of private use packed into one. Add long
            shifts, multiple drivers, commercial-grade wear and a buyer pool that is judging earning
            potential rather than looks, and the curve bends much harder.
          </p>
          <RetentionChart curve={model?.curve || 'car'} age={result?.age ?? 0} />
          <div className="tv-legend">
            <span className="tv-legend-item tv-legend-taxi">Commercial taxi</span>
            <span className="tv-legend-item tv-legend-private">Same model, private use</span>
          </div>

          <div className="tv-factors">
            <div className="tv-factor">
              <h3>Kilometres, not years</h3>
              <p>
                Age alone tells you nothing here. A 3-year-old taxi with 1.8 lakh km is perfectly
                normal; the same reading on a private car would be alarming. We price your odometer
                against what a working vehicle of that age should have run, so genuinely low-km
                taxis are rewarded instead of being punished by a private-car yardstick.
              </p>
            </div>
            <div className="tv-factor">
              <h3>The permit on the RC</h3>
              <p>
                A yellow-plate commercial vehicle sells only to operators. The same model on a white
                plate sells to them <em>and</em> to families, with no permit or fitness burden to
                inherit — which is worth roughly 12% more at resale. An All India Tourist Permit
                sits in between, since it widens the buyer pool across states.
              </p>
            </div>
            <div className="tv-factor">
              <h3>A legal expiry date</h3>
              <p>
                In NCR a diesel commercial vehicle is finished at 10 years and a petrol or CNG one at
                15. The market prices that in two to three years early: the discount deepens every
                month you hold it, and past the cap the vehicle can only be sold out of state or
                scrapped. No private-car calculator accounts for this.
              </p>
            </div>
            <div className="tv-factor">
              <h3>Running cost is the buyer's income</h3>
              <p>
                Your buyer is doing arithmetic on fuel per kilometre, because that is what they earn
                on. Factory CNG carries a premium, petrol runs at a discount for commercial duty, and
                a pre-2020 BS4 diesel now sells for less every year as restrictions tighten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="tv-faq">
        <div className="tv-faq-inner">
          <h2>Taxi Valuation — Common Questions</h2>
          <div className="tv-faq-list">
            {FAQS.map(({ q, a }, i) => (
              <details key={i} className="tv-faq-item">
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
          <p className="tv-faq-more">
            Want the longer version? Read our{' '}
            <Link to="/blog/taxi-valuation-delhi">taxi valuation guide for Delhi</Link> or{' '}
            <Link to="/blog/how-to-sell-taxi-fast-india">how to sell your taxi fast</Link>.
          </p>
        </div>
      </section>

      {/* Lead capture — a calculator answer is exactly when a seller is ready to talk */}
      <section className="tv-cta">
        <h2>Want a Firm Number Against Real Buyers?</h2>
        <p>
          The calculator prices the market. Our team prices <em>your</em> vehicle — against buyers
          who are looking for it right now. No charge, no obligation.
        </p>
        <div className="tv-cta-form">
          <LeadCaptureForm
            source="taxi-valuation"
            requestType="valuation_lead"
            heading="Get a free valuation call"
            subheading="We'll confirm the number against live buyer demand and tell you what it will actually sell for."
            buttonText="Get My Free Valuation"
            defaultMessage={leadMessage}
            messagePlaceholder="Anything else about the vehicle? (optional)"
          />
        </div>
      </section>

    </div>
  );
};

export default TaxiValuation;
